import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ChevronDown } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useSurveyUrl } from "../hooks/useSurveyUrl";
import { trackSurveyStart } from "../lib/track";
import { fontSans, fontMono, color as T, radius } from "../design/tokens";

// Phase 3 — /matching-model
//
// The canonical technical explainer for HAEVN's compatibility model, rewritten
// under the approved Geist visual system. Editorial long-form architecture.
// This page is public-facing; it explains the SHAPE of the model without
// exposing proprietary mechanics.
//
// Strict copy contracts (user-approved):
//   • The system is RULES-DRIVEN, not purely deterministic. Gates are
//     deterministic binary requirements; Weights measure relative compatibility.
//   • AI TRANSLATES structured results into readable explanations. AI does not
//     decide who someone will love.
//   • Match Score is a measurement of compatibility within HAEVN, NOT a
//     probability of relationship success.
//   • A Match requires 80%+ mutual Match Score. Recommendations sit at 77–79%
//     (blind unless both members accept).
//   • Match Monday is a WEEKLY SCHEDULED RELEASE. No continuous-scanning,
//     always-on, or real-time-browsing language.
//   • Mutual Qualification (both sides independently qualify) is a SEPARATE
//     concept from the Connection Model.
//   • "Connection is mutual by design" is reserved for the Connection Model
//     section only.
//   • No exact signal weights, no scoring formulas, no reveal of proprietary
//     mechanics.
//
// Approved outbound links only:
//   /how-it-works#matching · /how-80-works · /match-monday · /membership

// ── Sections (single source of truth for the TOC + headings) ────────
const SECTIONS = [
  { id: "system-overview", label: "System Overview" },
  { id: "declared-intent", label: "Declared Intent" },
  { id: "engine", label: "How the Engine Operates" },
  { id: "ai", label: "Rules-Driven. AI Translates." },
  { id: "gates", label: "Gates" },
  { id: "weights", label: "Weights" },
  { id: "mutual-qualification", label: "Mutual Qualification" },
  { id: "threshold-logic", label: "Threshold Logic" },
  { id: "verification", label: "Verification" },
  { id: "local-networks", label: "Local Networks" },
  { id: "release-architecture", label: "Weekly Release" },
  { id: "visibility", label: "Visibility" },
  { id: "connection-model", label: "Connection Model" },
];

// ── Typography helpers scoped to this page ──────────────────────────
const eyebrow = {
  fontFamily: fontMono,
  fontSize: 12,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 600,
  color: T.inkMuted,
};

const monoSupport = {
  fontFamily: fontMono,
  fontSize: 12,
  letterSpacing: "0.06em",
  fontWeight: 500,
  textTransform: "uppercase",
  color: T.inkMuted,
};

const h1 = {
  fontFamily: fontSans,
  fontSize: "clamp(36px, 4.6vw, 60px)",
  lineHeight: 1.05,
  letterSpacing: "-0.025em",
  fontWeight: 500,
  color: T.ink,
  margin: 0,
};

const h2 = {
  fontFamily: fontSans,
  fontSize: "clamp(22px, 2.4vw, 32px)",
  lineHeight: 1.15,
  letterSpacing: "-0.018em",
  fontWeight: 500,
  color: T.ink,
  margin: 0,
};

const h3 = {
  fontFamily: fontSans,
  fontSize: "clamp(16px, 1.4vw, 18px)",
  lineHeight: 1.3,
  letterSpacing: "-0.005em",
  fontWeight: 600,
  color: T.ink,
  margin: 0,
};

const body = {
  fontFamily: fontSans,
  fontSize: 17,
  lineHeight: 1.7,
  color: T.ink,
  fontWeight: 400,
};

const bodyMuted = { ...body, color: T.inkMuted };

// ── Understated contextual outbound link ────────────────────────────
function ContextLink({ to, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      style={{
        fontFamily: fontMono,
        fontSize: 12,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        fontWeight: 600,
        color: T.ink,
        borderBottom: `1px solid ${T.ink}`,
        paddingBottom: 2,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {children}
      <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
    </Link>
  );
}

// ── Lifecycle diagram — Define → Gate → Weight → Qualify → Release → Connect
function LifecycleDiagram() {
  const steps = ["Define", "Gate", "Weight", "Qualify", "Release", "Connect"];
  return (
    <figure
      data-testid="mm-lifecycle"
      className="my-10 md:my-12"
      aria-label="Matching lifecycle: Define, Gate, Weight, Qualify, Release, Connect"
    >
      <div className="overflow-x-auto -mx-2 px-2 pb-2">
        <ol className="flex items-stretch min-w-max gap-0">
          {steps.map((s, i) => (
            <li key={s} className="flex items-stretch">
              <div
                style={{
                  fontFamily: fontMono,
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: T.ink,
                  border: `1px solid ${T.line}`,
                  padding: "12px 18px",
                  borderRadius: radius.sm,
                  background: T.bg,
                }}
              >
                {s}
              </div>
              {i < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className="self-center mx-3"
                  style={{ color: T.accent, fontFamily: fontMono, fontSize: 14 }}
                >
                  →
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </figure>
  );
}

// ── Mutual qualification — two independent evaluations meeting in the middle
function MutualQualificationDiagram() {
  const cell = {
    border: `1px solid ${T.line}`,
    padding: "20px 18px",
    background: T.bg,
    borderRadius: radius.sm,
    textAlign: "center",
  };
  return (
    <figure
      data-testid="mm-mutual-diagram"
      className="my-10"
      aria-label="Mutual qualification: two independent evaluations"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-center">
        <div style={cell}>
          <p style={{ ...eyebrow, color: T.accent }}>Member A</p>
          <p className="mt-3" style={{ ...body, fontSize: 15 }}>
            Independently evaluated against B&rsquo;s criteria.
          </p>
        </div>
        <div className="text-center">
          <p
            style={{
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontWeight: 600,
              color: T.inkMuted,
            }}
          >
            Both must qualify
          </p>
        </div>
        <div style={cell}>
          <p style={{ ...eyebrow, color: T.accent }}>Member B</p>
          <p className="mt-3" style={{ ...body, fontSize: 15 }}>
            Independently evaluated against A&rsquo;s criteria.
          </p>
        </div>
      </div>
    </figure>
  );
}

// ── Threshold bar — 80% Match line + 77–79% Recommendation band ─────
function ThresholdDiagram() {
  return (
    <figure
      data-testid="mm-threshold"
      className="my-10 md:my-12"
      aria-label="Compatibility threshold: 80% Match, 77–79% Recommendation"
    >
      <div style={{ position: "relative", height: 4, background: T.line, marginTop: 36 }}>
        {/* Filled 0–80 range (teal, low opacity) */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            right: "20%",
            background: T.accent,
            opacity: 0.18,
          }}
        />
        {/* 77–79% recommendation band */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "77%",
            right: "21%",
            top: -6,
            bottom: -6,
            background: T.accent,
            opacity: 0.35,
          }}
        />
        {/* 80% hairline */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "80%",
            top: -12,
            bottom: -12,
            width: 1,
            background: T.accent,
          }}
        />
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "80%",
            top: -30,
            transform: "translateX(-50%)",
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: T.accent,
            whiteSpace: "nowrap",
          }}
        >
          80% Match
        </span>
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "78%",
            top: 16,
            transform: "translateX(-50%)",
            fontFamily: fontMono,
            fontSize: 10,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: T.inkMuted,
            whiteSpace: "nowrap",
          }}
        >
          77–79% Recs
        </span>
      </div>
      <div
        className="mt-14 flex justify-between"
        style={{
          fontFamily: fontMono,
          fontSize: 11,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: T.inkMuted,
        }}
      >
        <span>Below threshold — not surfaced</span>
        <span>Match</span>
      </div>
    </figure>
  );
}

// ── Local networks — three discrete city clusters ───────────────────
function LocalNetworkDiagram() {
  const cities = ["Austin", "Portland", "Tampa / St. Pete"];
  return (
    <figure
      data-testid="mm-networks"
      className="my-10"
      aria-label="Local network architecture: independent city networks"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cities.map((c) => (
          <div
            key={c}
            style={{
              border: `1px solid ${T.line}`,
              background: T.bg,
              padding: "20px 18px",
              borderRadius: radius.sm,
              textAlign: "center",
            }}
          >
            <p style={{ ...eyebrow, color: T.accent }}>Local network</p>
            <p
              className="mt-3"
              style={{
                fontFamily: fontSans,
                fontWeight: 500,
                fontSize: 18,
                letterSpacing: "-0.01em",
                color: T.ink,
              }}
            >
              {c}
            </p>
            <div className="mt-4 grid grid-cols-3 gap-1.5">
              {Array.from({ length: 9 }).map((_, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  className="block h-1.5 w-1.5 rounded-full mx-auto"
                  style={{ background: `${T.accent}66` }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </figure>
  );
}

// ── Table of contents ───────────────────────────────────────────────
function TOC({ activeId, onJump }) {
  return (
    <nav
      aria-label="Page contents"
      className="hidden lg:block sticky top-28 self-start"
    >
      <p style={{ ...monoSupport, fontSize: 11, letterSpacing: "0.14em" }}>
        On this page
      </p>
      <ol
        className="mt-4 space-y-2.5 pl-4"
        style={{ borderLeft: `1px solid ${T.line}` }}
      >
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                onJump(s.id);
              }}
              data-testid={`mm-toc-${s.id}`}
              className="block text-sm transition-colors duration-200"
              style={{
                fontFamily: fontSans,
                color: activeId === s.id ? T.ink : T.inkMuted,
                fontWeight: activeId === s.id ? 500 : 400,
              }}
            >
              {s.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function MobileTOC({ activeId, onJump }) {
  const [open, setOpen] = useState(false);
  const activeLabel =
    SECTIONS.find((s) => s.id === activeId)?.label || "Jump to section";
  return (
    <details
      className="lg:hidden mb-10"
      style={{ border: `1px solid ${T.line}`, borderRadius: radius.sm, background: T.bg }}
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="mm-mobile-toc"
    >
      <summary
        className="flex items-center justify-between px-4 py-3 cursor-pointer list-none"
        style={{ fontFamily: fontSans }}
      >
        <span style={{ fontSize: 14, color: T.ink, fontWeight: 500 }}>
          {activeLabel}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: T.inkMuted }}
        />
      </summary>
      <ol
        className="px-4 py-3 space-y-2"
        style={{ borderTop: `1px solid ${T.line}` }}
      >
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                onJump(s.id);
              }}
              className="block py-1"
              style={{
                fontFamily: fontSans,
                fontSize: 14,
                color: activeId === s.id ? T.ink : T.inkMuted,
                fontWeight: activeId === s.id ? 500 : 400,
              }}
            >
              {s.label}
            </a>
          </li>
        ))}
      </ol>
    </details>
  );
}

// ── Section shell + bullet list ─────────────────────────────────────
function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 pt-14 first:pt-0">
      <h2 style={h2}>{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Para({ children, muted = false }) {
  return (
    <p className="mt-5" style={muted ? bodyMuted : body}>
      {children}
    </p>
  );
}

function SubHeading({ children }) {
  return (
    <h3 className="mt-10" style={h3}>
      {children}
    </h3>
  );
}

function BulletList({ items, testId }) {
  return (
    <ul className="mt-5 space-y-2.5 list-none" data-testid={testId}>
      {items.map((item) => (
        <li
          key={item}
          className="pl-5 relative"
          style={{ ...body, fontSize: 16, lineHeight: 1.7 }}
        >
          <span
            aria-hidden="true"
            className="absolute left-0"
            style={{
              top: "0.8em",
              height: 4,
              width: 4,
              borderRadius: 999,
              background: T.accent,
            }}
          />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function MatchingModelPage() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const observerRef = useRef(null);
  const SURVEY_URL = useSurveyUrl();

  // Scroll-spy for the TOC.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    observerRef.current = observer;
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleJump = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, "", `#${id}`);
      }
    }
  };

  return (
    <div
      className="min-h-screen"
      data-testid="matching-model-page"
      style={{ background: T.bg, fontFamily: fontSans }}
    >
      <SEO
        title="The HAEVN Matching Model · HAEVN"
        description="How HAEVN's rules-driven compatibility model works. Declared Intent, Gates, Weights, Mutual Qualification, and the weekly Match Monday release — explained plainly, without exposing proprietary mechanics."
        path="/matching-model"
        type="article"
      />
      <Navigation />

      <main>
        {/* ── HEADER BAND ─────────────────────────────────────────── */}
        <header
          style={{ borderBottom: `1px solid ${T.line}`, background: T.bg }}
        >
          <div className="mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20">
            <p data-testid="mm-eyebrow" style={eyebrow}>
              Product Architecture
            </p>
            <h1
              data-testid="mm-h1"
              className="mt-5"
              style={{ ...h1, maxWidth: "20ch" }}
            >
              The HAEVN matching model.
            </h1>
            <p
              data-testid="mm-subhead"
              className="mt-6"
              style={{ ...bodyMuted, fontSize: 19, lineHeight: 1.5, maxWidth: "58ch" }}
            >
              A rules-driven compatibility system: Declared Intent first,
              deterministic Gates, relative Weights, mutual qualification, and
              a weekly release. What follows is the shape of the model — not
              the machinery inside it.
            </p>
          </div>
        </header>

        {/* ── BODY GRID ───────────────────────────────────────────── */}
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px] gap-12 lg:gap-16">
            <article className="max-w-[760px]">
              <MobileTOC activeId={activeId} onJump={handleJump} />

              {/* N/01 — System Overview */}
              <Section id="system-overview" title="N/01 · System Overview">
                <Para>
                  Every potential match moves through the same structured
                  lifecycle: members define compatibility criteria, the system
                  applies deterministic Gates, Weights measure relative
                  compatibility, both sides must qualify independently, and
                  qualified results are surfaced on a weekly schedule.
                </Para>
                <SubHeading>Matching lifecycle</SubHeading>
                <p
                  className="mt-4"
                  style={{
                    fontFamily: fontMono,
                    fontSize: 13,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    color: T.ink,
                  }}
                >
                  Define → Gate → Weight → Qualify → Release → Connect
                </p>
                <LifecycleDiagram />
                <Para muted>
                  The rest of this page walks each stage in order.
                </Para>
              </Section>

              {/* N/02 — Declared Intent */}
              <Section
                id="declared-intent"
                title="N/02 · Compatibility begins with declared intent."
              >
                <Para>
                  Most modern matching systems rely on inferred behavioural
                  signals — swipes, likes, click behaviour, dwell time,
                  engagement loops. Those systems try to guess intent from
                  reactive activity.
                </Para>
                <Para>HAEVN takes a different approach.</Para>
                <Para>
                  Everything begins with explicit member input. The survey
                  establishes the structured compatibility criteria the system
                  will use, before behavioural interaction becomes relevant at
                  all.
                </Para>
                <SubHeading>Compatibility dimensions may include</SubHeading>
                <BulletList
                  testId="mm-intent-dims"
                  items={[
                    "Relationship goals and Shared Intent",
                    "Relationship structure preferences",
                    "Orientation and gender compatibility",
                    "Geographic relevance",
                    "Age parameters",
                    "Values and boundaries",
                    "Lifestyle and communication style",
                    "Emotional and interpersonal indicators",
                  ]}
                />
                <Para>
                  The goal is not to predict chemistry. The goal is to
                  establish a structured framework for deciding whether an
                  introduction is worth the members&rsquo; time.
                </Para>
              </Section>

              {/* N/03 — How the Engine Operates */}
              <Section id="engine" title="N/03 · How the engine operates.">
                <Para>
                  Once a member&rsquo;s profile and qualification criteria are
                  established, they become a <em>standing query</em> against the active local network.
                </Para>
                <Para>
                  Traditional browsing platforms require the member to search,
                  filter, review, and repeatedly evaluate candidates. HAEVN
                  reverses that model: the member defines the qualification
                  logic once, and the system evaluates the network against it.
                </Para>
                <Para>
                  Evaluation is not a live browsing feed. It runs on a <strong style={{ fontWeight: 500 }}>weekly cadence</strong> tied to the Match Monday release. There is no continuous
                  scanning, no always-on scoring loop for members to check.
                </Para>
              </Section>

              {/* N/04 — Rules-Driven. AI Translates. */}
              <Section id="ai" title="N/04 · Rules-driven. AI translates, it doesn’t decide.">
                <Para>
                  The matching itself is <strong style={{ fontWeight: 500 }}>rules-driven</strong>. A
                  structured system of Gates and Weights reads member answers
                  and produces a Match Score. It is not a generative model,
                  and it is not a black-box recommendation engine.
                </Para>
                <Para>
                  AI is used only to <strong style={{ fontWeight: 500 }}>translate</strong> the
                  structured result into a readable, human explanation of <em>why</em> a Match is a Match — the shared signals, the
                  common ground, the tensions worth naming.
                </Para>
                <Para>
                  AI does not decide who someone will love. It does not score
                  attraction. It does not rank chemistry. Those questions
                  aren&rsquo;t engineering problems, and HAEVN isn&rsquo;t
                  trying to answer them.
                </Para>
              </Section>

              {/* N/05 — Gates */}
              <Section id="gates" title="N/05 · Gates: deterministic requirements.">
                <Para>
                  Some compatibility criteria function as absolute requirements
                  rather than weighted preferences. Those are <strong style={{ fontWeight: 500 }}>Gates</strong>.
                </Para>
                <Para>
                  A Gate is a <strong style={{ fontWeight: 500 }}>
                    deterministic binary
                  </strong> qualification condition. If a candidate fails a required
                  Gate, evaluation ends and the candidate is discarded from
                  that member&rsquo;s network — no further scoring occurs.
                </Para>
                <SubHeading>Gates may include</SubHeading>
                <BulletList
                  testId="mm-gates-list"
                  items={[
                    "Active local market eligibility",
                    "Geographic proximity",
                    "Age compatibility",
                    "Orientation compatibility",
                    "Gender compatibility",
                    "Relationship structure compatibility",
                    "Declared hard exclusions",
                  ]}
                />
                <Para>
                  Gates prevent compatibility inflation between members who
                  may share broad similarities but fail a fundamental
                  requirement.
                </Para>
              </Section>

              {/* N/06 — Weights */}
              <Section id="weights" title="N/06 · Weights: relative compatibility.">
                <Para>
                  Once a candidate clears every Gate, evaluation moves into <strong style={{ fontWeight: 500 }}>Weights</strong>.
                </Para>
                <Para>
                  Not every compatibility signal carries equal importance. A
                  system that treats all preferences equally tends to overstate
                  compatibility by rewarding superficial overlap. Weights let
                  HAEVN reflect the <em>relative</em> importance of different signals — how much
                  each contributes to the overall picture of fit, in
                  proportion to the others.
                </Para>
                <Para>
                  This page does not publish the specific weights the system
                  uses. Those are part of the model, not the marketing. What
                  matters here is the shape: identity, intent, and structural
                  compatibility carry more weight than surface-level
                  overlaps.
                </Para>
                <Para muted>
                  Weights allow the system to distinguish between broad
                  similarity and meaningful compatibility. The output is a <em>Match Score</em>: a measurement of compatibility within
                  the HAEVN system, not a probability of relationship
                  success.
                </Para>
              </Section>

              {/* N/07 — Mutual Qualification */}
              <Section
                id="mutual-qualification"
                title="N/07 · Mutual qualification."
              >
                <Para>
                  Compatibility must work in both directions.
                </Para>
                <Para>
                  A candidate is not qualified simply because they strongly
                  fit one member&rsquo;s stated preferences. The system
                  evaluates fit <strong style={{ fontWeight: 500 }}>
                    from each member&rsquo;s perspective independently
                  </strong>
                  , using that member&rsquo;s Gates and Weights.
                </Para>
                <MutualQualificationDiagram />
                <Para>
                  A candidate may clear the threshold for one member while
                  failing to clear it for the other. In that case, no Match is
                  surfaced. Both sides must qualify — separately — before an
                  introduction becomes possible.
                </Para>
                <Para muted>
                  This is a qualification requirement, not the connection
                  step. What happens <em>after</em> a Match is surfaced is covered in the
                  Connection Model below.
                </Para>
              </Section>

              {/* N/08 — Threshold Logic */}
              <Section id="threshold-logic" title="N/08 · Threshold logic.">
                <SubHeading>Why matching begins at 80%.</SubHeading>
                <Para>
                  The baseline threshold for a <strong style={{ fontWeight: 500 }}>Match</strong> is 80%
                  mutual compatibility. Below that, an introduction has not
                  earned the word Match, and HAEVN does not surface it as
                  one.
                </Para>
                <ThresholdDiagram />
                <Para>
                  Lower thresholds produce larger volumes of introductions,
                  but also noise, weaker fit, and member fatigue. Higher
                  thresholds reduce volume while improving confidence that
                  what does surface is worth attention.
                </Para>
                <SubHeading>Recommendations sit at 77–79%.</SubHeading>
                <Para>
                  Some pairings fall just below the Match threshold — in the <strong style={{ fontWeight: 500 }}>77–79%</strong> range.
                  These are surfaced as <strong style={{ fontWeight: 500 }}>Recommendations</strong> rather than Matches: blind by default, and only revealed to
                  both members if both accept.
                </Para>
                <Para muted>
                  Neither threshold is presented as a guarantee of chemistry
                  or relationship success. No structured model can reliably
                  predict human chemistry. The practical objective is
                  narrower: improve introduction quality by requiring
                  meaningful compatibility before connection becomes possible.
                </Para>
              </Section>

              {/* N/09 — Verification */}
              <Section id="verification" title="N/09 · Verification.">
                <Para>
                  Structured matching is only as reliable as the dataset it
                  operates against. HAEVN includes identity verification via <strong style={{ fontWeight: 500 }}>Veriff</strong> because
                  compatibility logic loses credibility inside a network
                  containing duplicate accounts, fabricated profiles, bots,
                  or anonymous participants with no accountability.
                </Para>
                <Para>
                  HAEVN retains the <em>result</em> of the verification check — pass or fail —
                  and does not store the underlying identity documents.
                </Para>
                <SubHeading>Verification supports</SubHeading>
                <BulletList
                  testId="mm-verif-list"
                  items={[
                    "Stronger dataset integrity",
                    "Reduced impersonation and fraud risk",
                    "Greater trust in surfaced matches",
                    "Lower spam risk",
                    "Real accountability inside the network",
                  ]}
                />
                <Para muted>
                  Verification does not affect compatibility scoring. Its role
                  is to strengthen trust in the participant layer the matching
                  engine evaluates.
                </Para>
              </Section>

              {/* N/10 — Local Networks */}
              <Section id="local-networks" title="N/10 · Local network architecture.">
                <Para>
                  HAEVN operates as a collection of <strong style={{ fontWeight: 500 }}>local networks</strong> rather than a single undifferentiated national pool. Every
                  member sits inside one active local network at a time.
                </Para>
                <LocalNetworkDiagram />
                <SubHeading>Why local, not national</SubHeading>
                <BulletList
                  testId="mm-local-list"
                  items={[
                    "Geographic viability — matches you can actually meet",
                    "Stronger local density over time",
                    "Fewer mathematically-interesting-but-impractical results",
                    "Real-world connection potential rather than a distant pen-pal",
                  ]}
                />
                <Para muted>
                  National-scale compatibility can produce fascinating pairings
                  that are impractical in real life. HAEVN prioritises local
                  network relevance because geographic viability materially
                  affects connection outcomes.
                </Para>
              </Section>

              {/* N/11 — Weekly Release */}
              <Section id="release-architecture" title="N/11 · Weekly release.">
                <Para>
                  Qualified introductions are not surfaced continuously. They
                  are released on a <strong style={{ fontWeight: 500 }}>weekly schedule</strong> — Match Monday.
                </Para>
                <Para>
                  Every Monday, each active member receives one of three
                  possible statuses: a Match (80%+), a Recommendation (77–79%),
                  or no new introduction that week. There is nothing to check
                  between Mondays, and no benefit to opening the app more
                  often.
                </Para>
                <Para>
                  This architecture intentionally separates compatibility
                  evaluation from engagement mechanics. Traditional browsing
                  platforms benefit when members return, swipe, and remain
                  behaviourally active. HAEVN doesn&rsquo;t.
                </Para>
                <div className="mt-6">
                  <ContextLink to="/match-monday" testId="mm-link-match-monday">
                    How Match Monday works
                  </ContextLink>
                </div>
              </Section>

              {/* N/12 — Visibility */}
              <Section id="visibility" title="N/12 · Visibility.">
                <Para>
                  HAEVN is not an open profile marketplace. Members are not
                  universally visible to one another.
                </Para>
                <Para>
                  Visibility is governed by compatibility qualification.
                  Exposure to another member happens only when structured
                  relevance exists — either as a Match or, with mutual
                  acceptance, as a revealed Recommendation.
                </Para>
                <Para>
                  Network Membership shows that a Match exists and provides
                  the Match explanation. <ContextLink to="/membership" testId="mm-link-membership">
                    HAEVN+
                  </ContextLink> is what unlocks photos and mutual introductions once both
                  members accept.
                </Para>
                <Para muted>
                  This visibility model reduces spam behaviour, low-fit
                  outreach, and popularity-driven distortions common in public
                  matching environments.
                </Para>
              </Section>

              {/* N/13 — Connection Model */}
              <Section id="connection-model" title="N/13 · Connection Model.">
                <Para>
                  A qualified Match creates the <em>opportunity</em> for connection. It does not automatically
                  create interaction.
                </Para>
                <Para>
                  <strong style={{ fontWeight: 500 }}>
                    Connection is mutual by design.
                  </strong> Both members must independently choose to proceed before a
                  conversation opens. Nothing is unlocked one-sidedly, and
                  there is no cold outreach dynamic — no unsolicited messages,
                  no browsing-and-picking a stranger.
                </Para>
                <Para>
                  This preserves member agency on both sides. Compatibility
                  gives you a reason to consider someone. The decision to
                  meet is still, and only, yours.
                </Para>
              </Section>

              {/* ── SUB-CONTEXT LINKS ────────────────────────────── */}
              <div
                className="mt-16 md:mt-20 flex flex-wrap gap-x-10 gap-y-5"
                data-testid="mm-context-links"
              >
                <ContextLink to="/how-it-works#matching" testId="mm-link-hiw">
                  How HAEVN works, end to end
                </ContextLink>
                <ContextLink to="/how-80-works" testId="mm-link-80">
                  Why 80%
                </ContextLink>
              </div>

              {/* ── SINGLE UNDERSTATED CTA ───────────────────────── */}
              <div
                data-testid="mm-cta"
                className="mt-16 md:mt-20 pt-10"
                style={{ borderTop: `1px solid ${T.line}` }}
              >
                <p style={{ ...eyebrow }}>Ready to begin?</p>
                <p
                  className="mt-3"
                  style={{
                    fontFamily: fontSans,
                    fontWeight: 500,
                    fontSize: "clamp(22px, 2.4vw, 30px)",
                    lineHeight: 1.2,
                    letterSpacing: "-0.014em",
                    color: T.ink,
                  }}
                >
                  Define your criteria. Let the system qualify.
                </p>
                <div className="mt-8 flex flex-col items-start gap-4">
                  <a
                    href={SURVEY_URL}
                    onClick={() => trackSurveyStart("matching_model_final_cta")}
                    className="hover:bg-black transition-colors duration-200"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "14px 28px",
                      background: T.ink,
                      color: "#fff",
                      borderRadius: radius.sm,
                      fontSize: 15,
                      fontWeight: 500,
                    }}
                    data-testid="mm-cta-btn"
                  >
                    Join the Network
                  </a>
                  <div style={monoSupport}>
                    Free&nbsp;·&nbsp;About 15 minutes&nbsp;·&nbsp;No app required
                  </div>
                </div>
                <div className="mt-14">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 hover:text-black transition-colors duration-200"
                    style={{ ...monoSupport, letterSpacing: "0.12em" }}
                    data-testid="mm-back-home"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                    Back to HAEVN
                  </Link>
                </div>
              </div>
            </article>

            <aside>
              <TOC activeId={activeId} onJump={handleJump} />
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
